import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Calendar, MapPin, Users, DollarSign, Plus, X } from 'lucide-react';
import { createEvent, editEvent } from '../../Fetch/fetchEvent';
import styles from './EventForm.module.css';

const EventForm = ({ event, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        status: 'ממתין',
        confirmUsersNumber: 0,
        value: 'ישיבת וועד', // Default to first option
        date: '',
        time: '',
        location: '',
        createdBy: '',
        buildingId: '',
        TopicsDiscussed: '',
        Decisions: '',
        topicsDiscussed: '',
        decisions: ''
    });

    // State for dynamic fields
    const [topicsDiscussed, setTopicsDiscussed] = useState(['']);
    const [decisions, setDecisions] = useState(['']);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Load event data if editing
    useEffect(() => {
        if (event) {
            setFormData(event);
            
            // Parse topics and decisions from strings if they exist
            // Check both capitalized and lowercase versions
            const topicsData = event.TopicsDiscussed || event.topicsDiscussed;
            if (topicsData) {
                try {
                    const topics = JSON.parse(topicsData);
                    setTopicsDiscussed(Array.isArray(topics) && topics.length > 0 ? topics : ['']);
                } catch (error) {
                    setTopicsDiscussed([topicsData]);
                }
            } else {
                setTopicsDiscussed(['']);
            }
            
            const decisionsData = event.Decisions || event.decisions;
            if (decisionsData) {
                try {
                    const decisions = JSON.parse(decisionsData);
                    setDecisions(Array.isArray(decisions) && decisions.length > 0 ? decisions : ['']);
                } catch (error) {
                    setDecisions([decisionsData]);
                }
            } else {
                setDecisions(['']);
            }
        } else {
            // Get building ID for new events
            try {
                const storedUser = localStorage.getItem('user');
                const user = storedUser ? JSON.parse(storedUser) : null;
                if (user?.buildingId) {
                    setFormData(prev => ({
                        ...prev,
                        buildingId: user.buildingId
                    }));
                }
            } catch (error) {
            }
        }
        setIsInitialLoad(false);
    }, [event]);

    // Reset topics and decisions when event type changes (but not on initial load)
    useEffect(() => {
        if (!isInitialLoad && formData.name !== 'ישיבת וועד') {
            setTopicsDiscussed(['']);
            setDecisions(['']);
        }
    }, [formData.name, isInitialLoad]);

    const validateForm = () => {
        const newErrors = {};

        // Basic validation
        if (!formData.name || !formData.name.trim()) {
            newErrors.name = 'יש לבחור סוג אירוע';
        }

        if (!formData.date) {
            newErrors.date = 'תאריך חובה';
        }

        if (!formData.time) {
            newErrors.time = 'שעה חובה';
        }

        if (!formData.location.trim()) {
            newErrors.location = 'מיקום חובה';
        }

        if (!formData.createdBy.trim()) {
            newErrors.createdBy = 'יוצר האירוע חובה';
        }

        if (!formData.value) {
            newErrors.value = 'סוג אירוע חובה';
        }

        // Validate number of participants
        if (formData.confirmUsersNumber < 0) {
            newErrors.confirmUsersNumber = 'מספר מאשרים לא יכול להיות שלילי';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Prepare data for submission
            const eventData = {
                ...formData,
                confirmUsersNumber: parseInt(formData.confirmUsersNumber) || 0,
            };

            // Add topics and decisions as JSON strings if event type is ישיבת וועד
            if (formData.name === 'ישיבת וועד') {
                const filteredTopics = topicsDiscussed.filter(topic => topic.trim());
                const filteredDecisions = decisions.filter(decision => decision.trim());
                
                // Save in both formats to ensure compatibility
                eventData.TopicsDiscussed = JSON.stringify(filteredTopics);
                eventData.Decisions = JSON.stringify(filteredDecisions);
                eventData.topicsDiscussed = JSON.stringify(filteredTopics);
                eventData.decisions = JSON.stringify(filteredDecisions);
            } else {
                eventData.TopicsDiscussed = '';
                eventData.Decisions = '';
                eventData.topicsDiscussed = '';
                eventData.decisions = '';
            }

            let result;

            if (event?.id) {
                result = await editEvent(event.id, eventData);
            } else {
                result = await createEvent(eventData);
            }

            if (onSave) {
                onSave(result);
            } else {
                onCancel();
            }

        } catch (error) {
            setErrors({ general: `אירעה שגיאה בשמירת האירוע: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? (value ? Number(value) : 0) : value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Functions for managing topics
    const addTopic = () => {
        setTopicsDiscussed(prev => [...prev, '']);
    };

    const removeTopic = (index) => {
        if (topicsDiscussed.length > 1) {
            setTopicsDiscussed(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updateTopic = (index, value) => {
        setTopicsDiscussed(prev => prev.map((topic, i) => i === index ? value : topic));
    };

    // Functions for managing decisions
    const addDecision = () => {
        setDecisions(prev => [...prev, '']);
    };

    const removeDecision = (index) => {
        if (decisions.length > 1) {
            setDecisions(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updateDecision = (index, value) => {
        setDecisions(prev => prev.map((decision, i) => i === index ? value : decision));
    };

    return (
        <div className={styles.eventFormContainer}>
            <div className={styles.formHeader}>
                <button className={styles.backButton} onClick={onCancel}>
                    <ArrowLeft size={20} />
                </button>
                <h2>{event ? 'ערוך אירוע' : 'צור אירוע חדש'}</h2>
            </div>

            {errors.general && (
                <div className={styles.errorMessage}>
                    {errors.general}
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.eventForm}>
                {/* Basic Information Section */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <h3>
                            <Calendar size={18} />
                            פרטים בסיסיים
                        </h3>
                        <p>הכנס את הפרטים הבסיסיים של האירוע</p>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="name">סוג אירוע *</label>
                            <select
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={errors.name ? styles.error : ''}
                            >
                                <option value="">בחר סוג אירוע</option>
                                <option value="ישיבת וועד">ישיבת וועד</option>
                                <option value="חגים">חגים</option>
                            </select>
                            {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="status">סטטוס</label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className={errors.status ? styles.error : ''}
                            >
                                <option value="ממתין">ממתין</option>
                                <option value="מאושר">מאושר</option>
                                <option value="מבוטל">מבוטל</option>
                                <option value="הסתיים">הסתיים</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="date">תאריך *</label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                className={errors.date ? styles.error : ''}
                            />
                            {errors.date && <span className={styles.fieldError}>{errors.date}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="time">שעה *</label>
                            <input
                                type="time"
                                id="time"
                                name="time"
                                value={formData.time}
                                onChange={handleInputChange}
                                className={errors.time ? styles.error : ''}
                            />
                            {errors.time && <span className={styles.fieldError}>{errors.time}</span>}
                        </div>
                    </div>
                </div>

                {/* Details Section */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <h3>
                            <MapPin size={18} />
                            פרטים נוספים
                        </h3>
                        <p>הוסף פרטים נוספים על האירוע</p>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="location">מיקום *</label>
                            <input
                                type="text"
                                id="location"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                className={errors.location ? styles.error : ''}
                                placeholder="מיקום האירוע"
                            />
                            {errors.location && <span className={styles.fieldError}>{errors.location}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="confirmUsersNumber">מספר מאשרים</label>
                            <input
                                type="number"
                                id="confirmUsersNumber"
                                name="confirmUsersNumber"
                                value={formData.confirmUsersNumber}
                                onChange={handleInputChange}
                                min="0"
                                className={errors.confirmUsersNumber ? styles.error : ''}
                                placeholder="0"
                            />
                            {errors.confirmUsersNumber && <span className={styles.fieldError}>{errors.confirmUsersNumber}</span>}
                        </div>
                    </div>
                </div>

                {/* Management Section */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <h3>
                            <Users size={18} />
                            ניהול האירוע
                        </h3>
                        <p>פרטי ניהול והרשאות</p>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="createdBy">נוצר על ידי *</label>
                            <input
                                type="text"
                                id="createdBy"
                                name="createdBy"
                                value={formData.createdBy}
                                onChange={handleInputChange}
                                className={errors.createdBy ? styles.error : ''}
                                placeholder="שם יוצר האירוע"
                            />
                            {errors.createdBy && <span className={styles.fieldError}>{errors.createdBy}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            {/* Empty for spacing */}
                        </div>
                    </div>
                </div>

                {/* Conditional Topics and Decisions Section for ישיבת וועד */}
                {formData.name === 'ישיבת וועד' && (
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <h3>
                                <Users size={18} />
                                תוכן הישיבה
                            </h3>
                            <p>נושאים והחלטות שנדונו בישיבה</p>
                        </div>

                        {/* Topics Discussed */}
                        <div className={styles.dynamicFieldGroup}>
                            <div className={styles.dynamicFieldHeader}>
                                <label>נושאים שנדונו</label>
                                <button 
                                    type="button" 
                                    className={styles.addButton}
                                    onClick={addTopic}
                                    title="הוסף נושא"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            
                            {topicsDiscussed.map((topic, index) => (
                                <div key={index} className={styles.dynamicFieldRow}>
                                    <input
                                        type="text"
                                        value={topic}
                                        onChange={(e) => updateTopic(index, e.target.value)}
                                        placeholder={`נושא ${index + 1}`}
                                        className={styles.dynamicFieldInput}
                                    />
                                    {topicsDiscussed.length > 1 && (
                                        <button
                                            type="button"
                                            className={styles.removeButton}
                                            onClick={() => removeTopic(index)}
                                            title="הסר נושא"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Decisions */}
                        <div className={styles.dynamicFieldGroup}>
                            <div className={styles.dynamicFieldHeader}>
                                <label>החלטות</label>
                                <button 
                                    type="button" 
                                    className={styles.addButton}
                                    onClick={addDecision}
                                    title="הוסף החלטה"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            
                            {decisions.map((decision, index) => (
                                <div key={index} className={styles.dynamicFieldRow}>
                                    <input
                                        type="text"
                                        value={decision}
                                        onChange={(e) => updateDecision(index, e.target.value)}
                                        placeholder={`החלטה ${index + 1}`}
                                        className={styles.dynamicFieldInput}
                                    />
                                    {decisions.length > 1 && (
                                        <button
                                            type="button"
                                            className={styles.removeButton}
                                            onClick={() => removeDecision(index)}
                                            title="הסר החלטה"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className={styles.formActions}>
                     <button type="submit" className={styles.saveButton} disabled={loading}>
                        {loading ? (
                            <span className={styles.loadingSpinner}></span>
                        ) : (
                            <Save size={18} />
                        )}
                        {loading ? 'שומר...' : (event ? 'עדכן אירוע' : 'צור אירוע')}
                    </button>
                    <button type="button" className={styles.cancelButton} onClick={onCancel}>
                        ביטול
                    </button>
                   
                </div>
            </form>
        </div>
    );
};

export default EventForm;